// Pinia store for the authenticated Moria user.
//
// Responsibilities:
//   - Own a lazily-populated reference to the current user (or null).
//   - Talk to the backend via the generated AuthService (cookies are HTTP-only;
//     the frontend never reads or writes them directly).
//   - Expose role-derived computed flags (isAdmin, canEditTasks) for guards
//     and UI gating.
//   - Kick off the Google OAuth flow with a full-page navigation (so the Vite
//     dev proxy, and in prod the reverse proxy, forwards /auth/google to the
//     backend — this MUST NOT be an SPA route).
//
// A 401 from /auth/me triggers a silent token rotation attempt. If the refresh
// succeeds the user remains authenticated; if it also fails, the store moves
// to `unauthenticated`. Non-401 errors set the `error` state instead.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { AuthService } from '@/api'
import { ApiError } from '@/api'
import type { AuthConfigOut, Capability, UserOut as User, UserSelfPatch } from '@/api'
import { getApiBaseUrl } from '@/api/env'
import { resetClientEventSession } from '@/observability/reporter'
import { resetExtractionLoadCaches } from '@/components/project/extractionLoadCaches'
import { useAssayEnvelopeExtractionsStore } from '@/stores/assayEnvelopeExtractions'
import { useAssayEnvelopeWorkspaceStore } from '@/stores/assayEnvelopeWorkspace'
import { useAssayEnvelopeReviewsStore } from '@/stores/assayEnvelopeReviews'
import { useChannelEnvelopeReviewsStore } from '@/stores/channelEnvelopeReviews'
import { useReviewDraftsStore } from '@/stores/reviewDrafts'
import { useMetadataStore } from '@/stores/metadata'
import { useNativeWorkbooksStore } from '@/stores/nativeWorkbooks'
import { useSessionStore } from '@/stores/session'
import { useSourceFilesStore } from '@/stores/sourceFiles'
import { useAssayColumnPreferencesStore } from '@/stores/assayColumnPreferences'
import { useWorkbookColumnPreferencesStore } from '@/stores/workbookColumnPreferences'
import { useSourceFilesPaneStore } from '@/stores/sourceFilesPane'
import { registeredMapOrigin, validatedMapCallback, type MapHandoffQuery } from '@/utils/mapHandoff'

type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'error'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const status = ref<AuthStatus>('idle')
  const error = ref<string | null>(null)
  let authGeneration = 0
  let preferenceOwner: string | null = null
  // Deployment-level capability flags from /auth/config. Fetched once at app
  // startup; readers should treat null as "not yet loaded".
  const config = ref<AuthConfigOut | null>(null)

  function setReviewStorageOwner(userId: string | null): void {
    if (preferenceOwner !== userId) {
      useAssayColumnPreferencesStore().clear()
      useWorkbookColumnPreferencesStore().clear()
      useSourceFilesPaneStore().clear()
      preferenceOwner = userId
    }
    useReviewDraftsStore().setStorageOwner(userId)
    useSessionStore().setStorageOwner(userId)
  }

  // Feature-capability flags (graceful degradation when API keys are unset).
  // Default to enabled while config is still null so there's no flash of
  // "unavailable" before /auth/config lands; a real missing key resolves to
  // false once it loads.
  const aiFeaturesEnabled = computed(() => config.value?.ai_features_enabled ?? true)
  const isAuthenticated = computed(() => status.value === 'authenticated' && user.value !== null)
  const role = computed(() => user.value?.role ?? null)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const canManageProjects = computed(() =>
    ['admin', 'collaborator'].includes(user.value?.role ?? ''),
  )
  const isQaQcSpecialist = computed(() => user.value?.role === 'qa_qc_specialist')
  const isCustomerViewer = computed(() => user.value?.role === 'customer_viewer')
  const hasCapability = (capability: Capability): boolean =>
    user.value?.capabilities?.includes(capability) ?? false
  const canReview = computed(
    () =>
      hasCapability('extractions:review') ||
      ['admin', 'collaborator', 'qa_qc_specialist'].includes(user.value?.role ?? ''),
  )
  const canReviewWorkbooks = computed(
    () =>
      !isCustomerViewer.value &&
      (hasCapability('workbooks:review') ||
        ['admin', 'collaborator', 'qa_qc_specialist'].includes(user.value?.role ?? '')),
  )
  const canCorrectReleasedWorkbooks = computed(
    () => canReviewWorkbooks.value || hasCapability('workbooks:review'),
  )
  const canDeliverWorkbooks = computed(() =>
    ['admin', 'collaborator'].includes(user.value?.role ?? ''),
  )
  const canManageWorkbooks = computed(() =>
    ['admin', 'collaborator'].includes(user.value?.role ?? ''),
  )
  const canManageExtractions = computed(() => hasCapability('extractions:manage'))
  const canEditTasks = computed(
    () =>
      hasCapability('extractions:manage') ||
      ['admin', 'collaborator'].includes(user.value?.role ?? ''),
  )

  async function fetchUser(): Promise<User | null> {
    const generation = ++authGeneration
    status.value = 'loading'
    error.value = null
    try {
      const me = await AuthService.meAuthMeGet()
      if (generation !== authGeneration) return null
      setReviewStorageOwner(me.id)
      user.value = me
      status.value = 'authenticated'
      return me
    } catch (err) {
      if (generation !== authGeneration) return null
      user.value = null
      const { status: httpStatus, message } = extractApiError(err)
      if (httpStatus === 401) {
        // The access token may have expired while the tab was idle (e.g. the
        // version-update banner was visible for >15 min before the user clicked
        // Refresh). Attempt a silent token rotation before giving up — if the
        // refresh token is still valid the user should not have to re-authenticate.
        try {
          await AuthService.refreshEndpointAuthRefreshPost()
        } catch (refreshError) {
          // Refresh token expired or absent — user must log in again.
          if (generation === authGeneration) {
            const failure = extractApiError(refreshError)
            if (failure.status === 401 || failure.status === 403) clearSession()
            else {
              status.value = 'error'
              error.value = failure.message
            }
          }
          return null
        }
        // Refresh succeeded — retry the session check with the new cookie.
        try {
          const me = await AuthService.meAuthMeGet()
          if (generation !== authGeneration) return null
          setReviewStorageOwner(me.id)
          user.value = me
          status.value = 'authenticated'
          return me
        } catch (retryErr) {
          if (generation !== authGeneration) return null
          user.value = null
          const { status: retryStatus, message: retryMessage } = extractApiError(retryErr)
          if (retryStatus === 401) {
            clearSession()
          } else {
            status.value = 'error'
            error.value = retryMessage
          }
        }
      } else {
        status.value = 'error'
        error.value = message
      }
      return null
    }
  }

  async function authorizeCustomerMap(state: string, challenge: string): Promise<string> {
    const result = await AuthService.authorizeMapAdminAuthMapAdminAuthorizePost({
      requestBody: { state, challenge },
    })
    // The backend selects the callback. Keep navigation constrained if its response is malformed.
    const target = new URL(result.callback_url)
    if (
      target.origin !== 'https://silverbowmaps.int.onmoria.com' ||
      target.pathname !== '/auth/platform/callback' ||
      target.search ||
      target.username ||
      target.password
    )
      throw new Error('Map sign-in could not be opened. Reopen the map from the workbook.')
    return target.href
  }

  async function authorizeMapAccess(query: MapHandoffQuery): Promise<string> {
    const generation = captureAuthGeneration()
    const result = await AuthService.authorizeMapAccessAuthMapAccessAuthorizePost({
      requestBody: {
        contract_version: 1,
        state: query.state,
        challenge: query.challenge,
        ...(query.client ? { client: query.client } : {}),
        ...(query.projectId ? { project_id: query.projectId } : {}),
        ...(query.customerHint ? { customer_id_hint: query.customerHint } : {}),
      },
    })
    if (!isAuthGenerationCurrent(generation))
      throw new Error('Your account changed during Map sign-in. Reopen the map.')
    if (result.contract_version !== 1)
      throw new Error('Map sign-in is temporarily unavailable. Try again shortly.')
    return validatedMapCallback(
      result.callback_url,
      query.state,
      registeredMapOrigin(useMetadataStore().environment, query.client),
    )
  }

  async function resolveMapEntryProject(customerHint: string): Promise<number> {
    const result = await AuthService.mapEntryAuthMapAccessEntryGet({ customerIdHint: customerHint })
    if (
      result.contract_version !== 1 ||
      !Number.isSafeInteger(result.project_id) ||
      result.project_id <= 0
    )
      throw new Error('Map access is temporarily unavailable. Try again shortly.')
    return result.project_id
  }

  function loginWithGoogle(returnTo?: string): void {
    // When VITE_API_URL is set (separate API domain), navigate there directly
    // so the __session and auth cookies are on the same origin as the API.
    // Firebase Hosting strips all non-__session cookies from proxied requests,
    // so the OAuth flow and all subsequent API calls must bypass Firebase.
    const apiBase = getApiBaseUrl() || window.location.origin
    const url = new URL('/auth/google', apiBase)
    if (returnTo) url.searchParams.set('return_to', returnTo)
    window.location.href = url.toString()
  }

  function resetExtractionState(): void {
    useAssayEnvelopeExtractionsStore().reset()
    useAssayEnvelopeWorkspaceStore().reset()
    useAssayEnvelopeReviewsStore().clear()
    useChannelEnvelopeReviewsStore().clear()
    useSourceFilesStore().reset()
    resetExtractionLoadCaches()
  }

  function resetWorkbookState(): void {
    useNativeWorkbooksStore().reset()
    useWorkbookColumnPreferencesStore().clear()
    useAssayColumnPreferencesStore().clear()
    useSourceFilesPaneStore().clear()
  }

  async function loginWithPassword(email: string, password: string): Promise<User> {
    const generation = ++authGeneration
    status.value = 'loading'
    error.value = null
    try {
      const me = await AuthService.loginAuthLoginPost({
        requestBody: { email, password },
      })
      if (generation !== authGeneration)
        throw new Error('A newer authentication attempt completed.')
      resetExtractionState()
      resetWorkbookState()
      setReviewStorageOwner(me.id)
      user.value = me
      status.value = 'authenticated'
      void useMetadataStore().refresh({ force: true })
      return me
    } catch (err) {
      if (generation !== authGeneration) {
        throw new Error('A newer authentication attempt completed.', { cause: err })
      }
      user.value = null
      const { status: httpStatus, message } = extractApiError(err)
      // 401 on login is a user-facing bad-credentials case, not a store
      // error state — keep the view in a clean "show the error inline" mode.
      status.value = 'unauthenticated'
      if (httpStatus === 401) {
        throw new Error('Invalid email or password', { cause: err })
      }
      throw new Error(message, { cause: err })
    }
  }

  async function acceptInvitation(token: string, password: string): Promise<User> {
    const generation = ++authGeneration
    status.value = 'loading'
    error.value = null
    const me = await AuthService.invitationAcceptAuthInvitationAcceptPost({
      requestBody: { token, password },
    })
    if (generation !== authGeneration) throw new Error('A newer authentication attempt completed.')
    resetExtractionState()
    resetWorkbookState()
    setReviewStorageOwner(me.id)
    user.value = me
    status.value = 'authenticated'
    void useMetadataStore().refresh({ force: true })
    return me
  }

  async function patchSelf(payload: UserSelfPatch): Promise<User> {
    const updated = await AuthService.patchMeAuthMePatch({
      requestBody: payload,
    })
    user.value = updated
    return updated
  }

  async function logout(): Promise<void> {
    resetClientEventSession()
    authGeneration += 1
    user.value = null
    status.value = 'unauthenticated'
    error.value = null
    resetExtractionState()
    resetWorkbookState()
    setReviewStorageOwner(null)
    void useMetadataStore().refresh({ force: true })
    try {
      await AuthService.logoutAuthLogoutPost()
    } catch {
      // Backend failures during logout must not block the client-side reset.
    }
  }

  function clearSession(): void {
    resetClientEventSession()
    user.value = null
    status.value = 'unauthenticated'
    error.value = null
    resetExtractionState()
    resetWorkbookState()
  }

  function invalidateSession(): void {
    authGeneration += 1
    clearSession()
  }

  function captureAuthGeneration(): number {
    return authGeneration
  }

  function isAuthGenerationCurrent(generation: number): boolean {
    return generation === authGeneration
  }

  function $reset(): void {
    invalidateSession()
    status.value = 'idle'
  }

  async function requestCustomerOtp(projectId: number, email: string): Promise<string> {
    const response = await AuthService.requestCustomerOtpAuthCustomerOtpRequestPost({
      requestBody: { project_id: projectId, email },
    })
    return response.message
  }

  async function verifyCustomerOtp(projectId: number, email: string, code: string): Promise<User> {
    const generation = ++authGeneration
    status.value = 'loading'
    error.value = null
    try {
      const me = await AuthService.verifyCustomerOtpAuthCustomerOtpVerifyPost({
        requestBody: { project_id: projectId, email, code },
      })
      if (generation !== authGeneration)
        throw new Error('A newer authentication attempt completed.')
      resetExtractionState()
      resetWorkbookState()
      setReviewStorageOwner(me.id)
      user.value = me
      status.value = 'authenticated'
      void useMetadataStore().refresh({ force: true })
      return me
    } catch (err) {
      if (generation !== authGeneration) {
        throw new Error('A newer authentication attempt completed.', { cause: err })
      }
      user.value = null
      status.value = 'unauthenticated'
      const { message } = extractApiError(err)
      throw new Error(message, { cause: err })
    }
  }

  return {
    user,
    status,
    error,
    config,
    aiFeaturesEnabled,
    isAuthenticated,
    role,
    isAdmin,
    canManageProjects,
    isQaQcSpecialist,
    isCustomerViewer,
    canReview,
    canReviewWorkbooks,
    canCorrectReleasedWorkbooks,
    canDeliverWorkbooks,
    canManageWorkbooks,
    canManageExtractions,
    canEditTasks,
    fetchUser,
    authorizeCustomerMap,
    authorizeMapAccess,
    resolveMapEntryProject,
    loginWithGoogle,
    loginWithPassword,
    requestCustomerOtp,
    verifyCustomerOtp,
    acceptInvitation,
    patchSelf,
    logout,
    invalidateSession,
    captureAuthGeneration,
    isAuthGenerationCurrent,
    $reset,
  }
})

// Normalizes thrown errors from the generated client (ApiError) and any
// plain objects/throwables that tests may inject.
function extractApiError(err: unknown): { status: number | undefined; message: string } {
  if (err instanceof ApiError) {
    return { status: err.status, message: err.message || err.statusText || 'Request failed' }
  }
  if (err && typeof err === 'object') {
    const anyErr = err as { status?: number; message?: string }
    return {
      status: typeof anyErr.status === 'number' ? anyErr.status : undefined,
      message: typeof anyErr.message === 'string' ? anyErr.message : 'Request failed',
    }
  }
  return { status: undefined, message: 'Request failed' }
}
